/** Operator's own dashboard — OPERATOR_OPS #6.
 *
 * Shown instead of the org-wide dashboard for operators (users who can
 * read sessions but aren't org admins). Surfaces THEIR stats: session
 * counts, working schedule + campaign load, and their upcoming sessions
 * — never the org-wide aggregates an admin sees. */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { StatCard } from "@/components/StatCard";
import { StatusPill } from "@/components/StatusPill";
import { ApiPaths, api } from "@/lib/api";
import { formatRelative } from "@/lib/format";
import type { Session } from "@/lib/types";

type OperatorDashboardResp = {
  sessions: {
    total: number;
    this_week: number;
    done: number;
    upcoming: number;
  };
  campaign_count: number;
  schedule: {
    has_schedule: boolean;
    weekly_hours: number;
    days_worked: number;
    timezone: string;
    upcoming_time_off: number;
  };
  upcoming_sessions: Session[];
};

export function OperatorDashboard() {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["operator-dashboard"],
    queryFn: async () =>
      (await api.get<OperatorDashboardResp>(ApiPaths.myOperatorDashboard))
        .data,
    refetchInterval: 60_000,
    placeholderData: (prev) => prev,
  });
  const d = q.data;
  const s = d?.schedule;

  return (
    <>
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label={t("operatorDashboard.statUpcoming", "Upcoming")}
          value={d?.sessions.upcoming ?? "—"}
          tone="info"
        />
        <StatCard
          label={t("operatorDashboard.statThisWeek", "This week")}
          value={d?.sessions.this_week ?? "—"}
          tone="ok"
        />
        <StatCard
          label={t("operatorDashboard.statDone", "Completed")}
          value={d?.sessions.done ?? "—"}
          tone="rose"
        />
        <StatCard
          label={t("operatorDashboard.statCampaigns", "My campaigns")}
          value={d?.campaign_count ?? "—"}
          tone="warn"
        />
      </section>

      {/* Schedule summary card. */}
      <section className="card">
        <h2 className="mb-2 text-sm font-semibold text-nb-text">
          {t("operatorDashboard.scheduleTitle", "My schedule")}
        </h2>
        {s?.has_schedule ? (
          <p className="text-sm text-nb-text-2">
            {t("operatorDashboard.scheduleSummary", {
              hours: s.weekly_hours,
              days: s.days_worked,
              tz: s.timezone,
              defaultValue: "{{hours}} h/week · {{days}} day(s) · {{tz}}",
            })}
            {s.upcoming_time_off > 0 && (
              <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-300">
                {t("operatorDashboard.timeOff", {
                  count: s.upcoming_time_off,
                  defaultValue: "{{count}} time-off upcoming",
                })}
              </span>
            )}
          </p>
        ) : (
          <p className="text-sm text-nb-text-3">
            {t(
              "operatorDashboard.noSchedule",
              "No working schedule set. Ask an admin to configure it.",
            )}
          </p>
        )}
      </section>

      {/* Upcoming sessions. */}
      <section className="card">
        <header className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold text-nb-text">
            {t("operatorDashboard.upcomingSessions", "My upcoming sessions")}
          </h2>
          <Link to="/sessions" className="text-sm text-nb-mint hover:underline">
            {t("dashboard.viewAllSessions")} →
          </Link>
        </header>
        {q.isLoading && (
          <p className="text-sm text-nb-text-2">{t("dashboard.loading")}</p>
        )}
        {d && d.upcoming_sessions.length === 0 && (
          <p className="text-sm text-nb-text-2">
            {t("operatorDashboard.noUpcoming", "No upcoming sessions.")}
          </p>
        )}
        {d && d.upcoming_sessions.length > 0 && (
          <ul className="divide-y divide-nb-border">
            {d.upcoming_sessions.map((sess) => (
              <li key={sess.id} className="-mx-2 rounded hover:bg-nb-surface/40">
                <Link
                  to={`/sessions/${sess.id}`}
                  className="flex items-center justify-between gap-3 px-2 py-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <StatusPill status={sess.status} />
                      <span className="truncate text-sm font-medium text-nb-text">
                        {sess.title ||
                          sess.notes ||
                          sess.campaign_name ||
                          sess.id.slice(0, 8)}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-nb-text-3">
                      {formatRelative(sess.scheduled_for || sess.created_at)}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
